import { formatDate, toDate as parseToDate, toDateString } from '@/utils/date';

import {
    ALL_TIME_SENTINEL_FROM,
    DATE_RANGE_PRESETS,
    type DateRangePreset,
    type DateRangePresetId,
    findMatchingPreset,
} from './DateRangePresets';

/** The range the toggle names and the menu opens with. */
export interface AppliedRange {
    from: Date | null;
    to: Date | null;
    /**
     * The preset the range matches, or null for custom days. `none` means the page
     * has not supplied its range yet, which is the only loading state.
     */
    presetId: DateRangePresetId | null;
    /** False when the days came from the default preset because the page passed none. */
    fromPage: boolean;
}

export interface ResolveAppliedRangeOptions {
    defaultPresetId: DateRangePresetId;
    include24h: boolean;
    includeLast3Days?: boolean;
    hiddenPresetIds?: readonly DateRangePresetId[];
}

/**
 * Works out what a range means for the picker: which preset it matches and which days
 * to show. The picker runs every range through here, the one a page passes and the
 * one each action sends, so an action ends in the same state as the page's echo of it.
 *
 * Days that do not parse count as absent, so a hand-edited URL falls back to the
 * default preset instead of leaving the picker without days.
 */
export function resolveAppliedRange(
    dateFrom: string | undefined,
    dateTo: string | undefined,
    options: ResolveAppliedRangeOptions
): AppliedRange {
    const from = parseToDate(dateFrom);
    const to = parseToDate(dateTo);

    if (from === null && to === null) {
        const defaults = DATE_RANGE_PRESETS.find(
            (preset) => preset.id === options.defaultPresetId
        )?.calculateDates();
        return {
            from: parseToDate(defaults?.dateFrom),
            to: parseToDate(defaults?.dateTo),
            presetId: options.defaultPresetId,
            fromPage: false,
        };
    }

    const presetId = findMatchingPreset(toDateString(from), toDateString(to), {
        include24h: options.include24h,
        includeLast3Days: options.includeLast3Days === true,
        hiddenPresetIds: options.hiddenPresetIds,
    });
    // "Show all" travels as the sentinel range, which is not worth showing as days.
    if (presetId === 'all') {
        return { from: null, to: null, presetId, fromPage: true };
    }
    return { from, to, presetId, fromPage: true };
}

/**
 * The range a preset sends. "Show all" sends the sentinel up to today rather than no
 * days, because endpoints that need a bounded range would otherwise apply their own
 * default.
 *
 * TODO: Backend should support empty date range for all preset and retry all data.
 */
export function presetRange(preset: DateRangePreset): {
    dateFrom: string | undefined;
    dateTo: string | undefined;
} {
    if (preset.id === 'all') {
        return { dateFrom: ALL_TIME_SENTINEL_FROM, dateTo: formatDate(new Date(), 'internal') };
    }
    return preset.calculateDates();
}
